/**
 * 展示层格式化。
 *
 * 后端一律返回「以分为单位的整数金额」与「整数灵力」，禁止浮点；
 * 除 100 这件事只在这里做一次。
 */

import { Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

export function fmtDateTime(v: string | null | undefined): string {
  return v ? dayjs(v).format("YYYY-MM-DD HH:mm:ss") : "—";
}

export function fmtDate(v: string | null | undefined): string {
  return v ? dayjs(v).format("YYYY-MM-DD") : "—";
}

/** 相对时间 + 绝对时间 tooltip。列表页看「多久以前」比看时间戳有用。 */
export function RelativeTime({ value }: { value: string | null | undefined }) {
  if (!value) return <span style={{ color: "#999" }}>—</span>;
  return (
    <Tooltip title={fmtDateTime(value)}>
      <span>{dayjs(value).fromNow()}</span>
    </Tooltip>
  );
}

export function fmtNumber(v: number | null | undefined): string {
  return typeof v === "number" ? v.toLocaleString() : "—";
}

/** 分 → 元。 */
export function fmtCents(cents: number | null | undefined): string {
  if (typeof cents !== "number") return "—";
  return `¥${(cents / 100).toFixed(2)}`;
}

export function fmtBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== "number" || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function fmtPercent(ratio: number | null | undefined, digits = 2): string {
  return typeof ratio === "number" ? `${(ratio * 100).toFixed(digits)}%` : "—";
}

const STATUS_COLORS: Record<string, string> = {
  // 通用
  active: "green",
  ok: "green",
  succeeded: "green",
  published: "green",
  ready: "green",
  pass: "green",
  exact: "green",
  running: "blue",
  pending: "gold",
  pending_upload: "gold",
  queued: "gold",
  draft: "default",
  stale: "orange",
  weakened: "orange",
  flag: "orange",
  warning: "orange",
  // 入口维护中。**不能与 archived 同色**：一个是临时停、一个是永久下线，
  // 而这一列上唯一能一眼分开它们的就是颜色。
  maintenance: "orange",
  superseded: "default",
  archived: "default",
  disabled: "default",
  closed: "default",
  failed: "red",
  dead: "red",
  blocked: "red",
  block: "red",
  quarantined: "red",
  critical: "red",
  suspended: "red",
  deleted: "red",
  deletion_requested: "orange",
  // 形态：normal 与 weakened 的模型单价差 12 倍，颜色要能一眼分开
  normal: "magenta",
  // 角色
  super_admin: "red",
  operator: "blue",
  // 消息角色
  user: "blue",
  assistant: "magenta",
  system: "default",
};

export function StatusTag({ value }: { value: string | null | undefined }) {
  if (!value) return <span style={{ color: "#999" }}>—</span>;
  return <Tag color={STATUS_COLORS[value] ?? "default"}>{value}</Tag>;
}

export function Tags({ values }: { values: string[] | null | undefined }) {
  if (!values || values.length === 0) return <span style={{ color: "#999" }}>—</span>;
  return (
    <>
      {values.map((v) => (
        <Tag key={v} style={{ marginInlineEnd: 4 }}>
          {v}
        </Tag>
      ))}
    </>
  );
}

/**
 * ID 缩写。
 *
 * **取尾部而不是头部。** 主键是 UUIDv7，前 48 位是毫秒时间戳，
 * 同一批迁移进来的行前缀完全相同——实测 1,479 个用户的前 8 位都是 `019fac8e`，
 * 截头部会让整列看起来是同一个 ID。判别信息全在尾部的随机段。
 */
export function shortId(value: string | null | undefined): string {
  if (!value) return "—";
  return value.length > 12 ? `…${value.slice(-8)}` : value;
}

/** 表格里放 36 位 UUID 会把列挤没。点击复制的是完整 ID。 */
export function ShortId({ value }: { value: string | null | undefined }) {
  if (!value) return <span style={{ color: "#999" }}>—</span>;
  return (
    <Tooltip title={value}>
      <Typography.Text copyable={{ text: value }} style={{ fontFamily: "monospace", fontSize: 12 }}>
        {shortId(value)}
      </Typography.Text>
    </Tooltip>
  );
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "var(--miao-color-text-muted)" }}>{children}</span>;
}

export function Empty() {
  return <span style={{ color: "#bbb" }}>—</span>;
}
